import { defineComponent as _defineComponent } from 'vue';
import { resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, mergeProps as _mergeProps, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "grid min-h-full grid-cols-[auto_1fr] bg-white" };
const _hoisted_2 = { class: "flex min-w-0 flex-col gap-4 bg-white px-5 pt-6 pb-4" };
const _hoisted_3 = { class: "flex" };
const _hoisted_4 = { class: "flex flex-col gap-2" };
const _hoisted_5 = { class: "flex flex-wrap items-center gap-3" };
const _hoisted_6 = {
    class: "text-2xl font-bold text-(--colors-text-heading) sm:text-3xl",
    "data-testid": "value-list-detail-title"
};
const _hoisted_7 = { class: "text-sm text-(--colors-text-heading)" };
const _hoisted_8 = { "data-testid": "value-list-detail-order-detail" };
const _hoisted_9 = ["data-testid"];
const _hoisted_10 = ["data-testid"];
const _hoisted_11 = {
    class: "sr-only",
    role: "status",
    "aria-live": "polite",
    "data-testid": "value-list-detail-announcement"
};
import { computed, h, ref, shallowRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { onBeforeRouteUpdate, RouterLink, useRoute } from "vue-router";
import { Badge, Button, DataTable, Dropdown, InlineLink, Pager, Popover, TableFilterSelector, TableShell, Tooltip, UnsavedChangesModal, useColumnSettings, useTableQuery, useTableViews, } from "@eudonet/opal";
import { resolveApiErrorKey } from "@/infrastructure/services/ApiErrorService";
import { moduleRegistry } from "@/router/moduleRegistry";
import ModuleState from "@/shared/components/ModuleState.vue";
import { useAnalyticsTracker } from "@/shared/composables/analytics/useAnalyticsTracker";
import { useSaveOrchestrator } from "@/shared/composables/forms/useSaveOrchestrator";
import { buildColumnSettingsLabels } from "@/shared/composables/columnSettingsLabels";
import { buildPagerLabels } from "@/shared/composables/pagerLabels";
import { buildTableLabels } from "@/shared/composables/tableLabels";
import { resolveLabel, valueListName } from "../../domain/valueList";
import { buildHierarchyRows, effectiveValue, filterHierarchyRows, } from "../../domain/valueListHierarchy";
import { canMoveFamily, canMovePin, familyCodes, isDefaultOrder, MOVE_DIRECTIONS, moveFamily, movePin, pin, pinnedValues, unpin, withDefaultOrder, } from "../../domain/valueListOrder";
import { identityLanguage, isDisplayed, isInStatusTab, matchesValueSearch, secondaryLanguages, VALUE_STATUS_TABS, valueStatus, visibilityLock, } from "../../domain/valueListValue";
import { useValueListStore } from "../../stores/useValueListStore";
import { useValueListValuesStore } from "../../stores/useValueListValuesStore";
import ValueDisplayDrawer from "../components/ValueDisplayDrawer.vue";
import { useValueListTable } from "../composables/useValueListTable";
const LANGUAGE_COLUMN_PREFIX = "label";
const SAVE_ERROR_FALLBACK = "valueLists.detail.errors.save";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ValueListDetailView',
    setup(__props) {
        const ROW_OPTIONS = [10, 30, 50];
        const { t, locale } = useI18n();
        const route = useRoute();
        const listsStore = useValueListStore();
        const valuesStore = useValueListValuesStore();
        const { secondaryLanguageHeaders, toSettingsColumns, tabDropdowns, filterSelectorLabels, inertFilterFields } = useValueListTable();
        const SidebarComponent = shallowRef(moduleRegistry.getComponentForSlot("sidebar"));
        const { track } = useAnalyticsTracker();
        const tableQuery = useTableQuery({ pageSize: ROW_OPTIONS[0], activeViewId: "all" });
        const tableLabels = computed(() => buildTableLabels(t));
        const pagerLabels = computed(() => buildPagerLabels(t));
        const code = computed(() => String(route.params.code));
        const list = computed(() => listsStore.lists.find((candidate) => candidate.code === code.value));
        /** Until the lists load, or for a code no list carries, the code is the only name there is. */
        const title = computed(() => (list.value ? valueListName(list.value, locale.value) : code.value));
        const isUnknownList = computed(() => listsStore.isLoaded && !list.value);
        const sortMode = computed(() => list.value?.sortMode ?? "Manual");
        const availableDefault = computed(() => list.value?.availableDefault ?? "Visible");
        const isLoading = computed(() => valuesStore.isLoading || listsStore.isLoading);
        const orderLabel = computed(() => t(sortMode.value === "Manual" ? "valueLists.detail.order.manual" : "valueLists.detail.order.alphabetical"));
        const orderDetail = computed(() => t(sortMode.value === "Manual"
            ? "valueLists.detail.order.manualDetail"
            : "valueLists.detail.order.alphabeticalDetail"));
        /** Retries only what failed. */
        function reload() {
            if (listsStore.loadError)
                void listsStore.load();
            if (valuesStore.loadError)
                void valuesStore.load(code.value);
        }
        watch(code, (next, previous) => {
            // The view is reused across lists: the previous list's page, tab and search mean nothing here.
            if (previous !== undefined) {
                tableQuery.setSearch("");
                tableQuery.setView("all");
            }
            tableQuery.setPage(1);
            void listsStore.ensureLoaded();
            // A code the loaded catalogue lacks shows the not-found state: there are no values to read.
            if (listsStore.isLoaded && !list.value)
                return;
            void valuesStore.load(next);
        }, { immediate: true });
        // ── Lines: categories, then their sub-values ─────────────────────────────────
        // Built on its own so a keystroke or a tab change only filters.
        const hierarchyRows = computed(() => buildHierarchyRows(valuesStore.effectiveValues, sortMode.value, locale.value));
        const isTwoLevelList = computed(() => hierarchyRows.value.some((row) => row.parent !== null));
        // ── Status tabs ──────────────────────────────────────────────────────────────
        const TAB_LABEL_KEYS = {
            all: "valueLists.detail.tabs.all",
            essential: "valueLists.detail.tabs.essential",
            displayed: "valueLists.detail.tabs.displayed",
            hidden: "valueLists.detail.tabs.hidden",
        };
        const tableViews = useTableViews({ initialViews: [] });
        const viewTabs = computed(() => VALUE_STATUS_TABS.map((tab) => ({
            id: tab,
            label: t(TAB_LABEL_KEYS[tab]),
            count: hierarchyRows.value.filter((row) => isInStatusTab(effectiveValue(row), tab, availableDefault.value))
                .length,
            isPreset: true,
            isDefault: tab === "all",
            querySnapshot: {},
        })));
        watch(viewTabs, (next) => (tableViews.views.value = next), { immediate: true });
        const activeTab = computed(() => {
            const id = tableQuery.state.activeViewId;
            return VALUE_STATUS_TABS.find((tab) => tab === id) ?? "all";
        });
        const filterFields = computed(() => inertFilterFields([
            { key: "origin", label: t("valueLists.detail.filter.origin"), icon: "ri-price-tag-3-line" },
            { key: "hierarchy", label: t("valueLists.detail.filter.hierarchy"), icon: "ri-node-tree" },
        ]));
        // ── Rows ─────────────────────────────────────────────────────────────────────
        const searchTerm = computed(() => tableQuery.state.search ?? "");
        const filteredRows = computed(() => filterHierarchyRows(hierarchyRows.value, (row) => isInStatusTab(effectiveValue(row), activeTab.value, availableDefault.value) &&
            matchesValueSearch(row.value, searchTerm.value)));
        /** The table reads values; each cell finds its line (level, category, path) here. */
        const rowByCode = computed(() => new Map(filteredRows.value.map((row) => [row.value.internalCode, row])));
        const totalPages = computed(() => Math.max(1, Math.ceil(filteredRows.value.length / tableQuery.state.pageSize)));
        /** A line of the table: a value, or the copy of a pinned value shown at the top of an alphabetical list. */
        const isAlphabetical = computed(() => sortMode.value === "Alphabetical");
        const lineByCode = computed(() => new Map(hierarchyRows.value.map((row) => [row.value.internalCode, row])));
        /**
         * The pinned block: a display shortcut, on the first page only and without a search, so it is never
         * counted in the tabs and never splits a result list. A copy follows the active tab like its value.
         */
        const pinCopies = computed(() => {
            if (!isAlphabetical.value || searchTerm.value || tableQuery.state.page !== 1)
                return [];
            return pinnedValues(valuesStore.effectiveValues)
                .filter((value) => {
                const line = lineByCode.value.get(value.internalCode);
                return !!line && isInStatusTab(effectiveValue(line), activeTab.value, availableDefault.value);
            })
                .map((value) => ({ ...value, pinCopy: true }));
        });
        const pageRows = computed(() => {
            const start = (tableQuery.state.page - 1) * tableQuery.state.pageSize;
            const page = filteredRows.value.slice(start, start + tableQuery.state.pageSize).map((row) => row.value);
            return [...pinCopies.value, ...page];
        });
        /** Copies and their values share an internal code; test ids tell them apart. */
        function rowTestId(row) {
            return row.pinCopy ? `pinned-${row.internalCode}` : row.internalCode;
        }
        function isPinShown(row) {
            return isAlphabetical.value && row.pinRank !== null;
        }
        /** Hidden by itself or through its category: the line reads disabled. */
        function isDisabledRow(row) {
            return isInStatusTab(effectiveValue(row), "hidden", availableDefault.value);
        }
        function isRowMuted(value) {
            const row = (value.pinCopy ? lineByCode : rowByCode).value.get(value.internalCode);
            return !!row && ((!value.pinCopy && row.isContext) || isDisabledRow(row));
        }
        // ── Moves and pins ───────────────────────────────────────────────────────────
        /** The lines the last move or pin acted on, shown as selected; any later action replaces them. */
        const highlighted = ref({ codes: new Set(), pinCopy: false });
        const announcement = ref("");
        function isRowHighlighted(value) {
            return !!value.pinCopy === highlighted.value.pinCopy && highlighted.value.codes.has(value.internalCode);
        }
        function clearHighlight() {
            highlighted.value = { codes: new Set(), pinCopy: false };
        }
        watch([code, searchTerm, activeTab], clearHighlight);
        const labelOf = (internalCode) => {
            const value = valuesStore.effectiveValues.find((candidate) => candidate.internalCode === internalCode);
            return value ? resolveLabel(value.labels, locale.value).text : internalCode;
        };
        /** A category or a sub-value: its whole family moves as one block, then reads as selected. */
        function moveValue(internalCode, direction) {
            const next = moveFamily(valuesStore.effectiveValues, internalCode, direction, locale.value);
            const family = familyCodes(next, internalCode, locale.value);
            valuesStore.setLayout(next, family);
            highlighted.value = { codes: new Set(family), pinCopy: false };
            // The family may land on another page (to top / to bottom): show the page it is on.
            const index = filteredRows.value.findIndex((row) => row.value.internalCode === family[0]);
            if (index >= 0)
                tableQuery.setPage(Math.floor(index / tableQuery.state.pageSize) + 1);
            const line = buildHierarchyRows(next, "Manual", locale.value).find((row) => row.value.internalCode === family[0]);
            announcement.value = t("valueLists.detail.order.moved", {
                value: labelOf(family[0]),
                rank: line?.rank ?? 0,
                total: line?.siblings ?? 0,
            });
            track("value_order_manual_move", { catalogCode: code.value, direction });
        }
        function restoreDefaultOrder() {
            const current = valuesStore.effectiveValues;
            const next = withDefaultOrder(current);
            valuesStore.setLayout(next, next.filter((value, i) => value !== current[i]).map((value) => value.internalCode));
            clearHighlight();
            announcement.value = t("valueLists.detail.order.restored");
            track("value_order_reset_default", { catalogCode: code.value });
        }
        function togglePin(internalCode, pinned) {
            const next = (pinned ? pin : unpin)(valuesStore.effectiveValues, internalCode);
            valuesStore.setLayout(next, [internalCode]);
            const rank = next.find((value) => value.internalCode === internalCode)?.pinRank ?? null;
            highlighted.value = { codes: new Set(pinned ? [internalCode] : []), pinCopy: true };
            announcement.value = pinned
                ? t("valueLists.detail.pin.announcePinned", { value: labelOf(internalCode), rank })
                : t("valueLists.detail.pin.announceUnpinned", { value: labelOf(internalCode) });
            track("value_pin_toggled", { catalogCode: code.value, pinned });
        }
        function moveCopy(internalCode, direction) {
            const next = movePin(valuesStore.effectiveValues, internalCode, direction);
            valuesStore.setLayout(next, [internalCode]);
            highlighted.value = { codes: new Set([internalCode]), pinCopy: true };
            announcement.value = t("valueLists.detail.pin.announceMoved", {
                value: labelOf(internalCode),
                rank: next.find((value) => value.internalCode === internalCode)?.pinRank ?? 0,
            });
        }
        // Once per opened list: loading a list empties the values first, so this turns false in between.
        watch(isTwoLevelList, (twoLevel) => {
            if (twoLevel)
                track("value_list_hierarchy_viewed", { catalogCode: code.value });
        }, { immediate: true });
        // ── Columns ──────────────────────────────────────────────────────────────────
        const languageColumnId = (language) => `${LANGUAGE_COLUMN_PREFIX}-${language}`;
        const columnHeaders = computed(() => ({
            identity: t("valueLists.detail.columns.identity", {
                code: identityLanguage(locale.value).toUpperCase(),
            }),
            ...secondaryLanguageHeaders(LANGUAGE_COLUMN_PREFIX),
            externalCode: t("valueLists.detail.columns.externalCode"),
            internalCode: t("valueLists.detail.columns.internalCode"),
            status: t("valueLists.detail.columns.status"),
            position: t("valueLists.detail.columns.position"),
        }));
        const columnSettings = useColumnSettings({
            columns: () => toSettingsColumns(columnHeaders.value, "identity", ["internalCode"]),
            defaultVisibility: { internalCode: false },
            defaultPinning: { left: ["identity"], right: [] },
            labels: () => buildColumnSettingsLabels(t),
        });
        const MOVE_ITEMS = {
            up: { key: "valueLists.detail.rowActions.moveUp", icon: "ri-arrow-up-line" },
            down: { key: "valueLists.detail.rowActions.moveDown", icon: "ri-arrow-down-line" },
            top: { key: "valueLists.detail.rowActions.moveToTop", icon: "ri-skip-up-line" },
            bottom: { key: "valueLists.detail.rowActions.moveToBottom", icon: "ri-skip-down-line" },
        };
        function moveItems(testIdPrefix, canMove, onMove, blockedReason = null) {
            return MOVE_DIRECTIONS.map((direction) => ({
                label: t(MOVE_ITEMS[direction].key),
                icon: MOVE_ITEMS[direction].icon,
                testId: `${testIdPrefix}-${direction}`,
                disabled: blockedReason !== null || !canMove(direction),
                ...(blockedReason === null ? {} : { tooltip: t(blockedReason) }),
                onClick: () => onMove(direction),
            }));
        }
        /**
         * A move ranks a value among every value of the list (every pin, for a pinned copy), so it is only
         * offered where they are all on screen: on the "All" tab, without a search.
         */
        const filteredMoveReason = computed(() => activeTab.value !== "all" || searchTerm.value ? "valueLists.detail.rowActions.filteredNoMove" : null);
        /**
         * Manual list: Edit and the moves (a move at its end is disabled). Alphabetical list: the moves stay
         * listed but disabled, with the reason, then pin or unpin. A pinned copy moves among the pins only.
         * A disabled line offers only "Modifier", except a pinned copy: its pin still takes a slot among
         * the others, so it can still be moved or unpinned.
         */
        function rowActions(value) {
            const edit = {
                label: t("valueLists.detail.rowActions.edit"),
                icon: "ri-pencil-line",
                testId: `value-list-edit-${rowTestId(value)}`,
                onClick: () => openDrawer(value),
            };
            const line = (value.pinCopy ? lineByCode : rowByCode).value.get(value.internalCode);
            if (line && isDisabledRow(line) && !value.pinCopy)
                return [edit];
            const values = valuesStore.effectiveValues;
            const unpinItem = {
                label: t("valueLists.detail.rowActions.unpin"),
                icon: "ri-unpin-line",
                testId: `value-list-unpin-${rowTestId(value)}`,
                onClick: () => togglePin(value.internalCode, false),
            };
            if (value.pinCopy) {
                return [
                    edit,
                    ...moveItems(`value-list-move-${rowTestId(value)}`, (direction) => canMovePin(values, value.internalCode, direction), (direction) => moveCopy(value.internalCode, direction), filteredMoveReason.value),
                    unpinItem,
                ];
            }
            if (!isAlphabetical.value) {
                return [
                    edit,
                    ...moveItems(`value-list-move-${value.internalCode}`, (direction) => canMoveFamily(values, value.internalCode, direction, locale.value), (direction) => moveValue(value.internalCode, direction), filteredMoveReason.value),
                ];
            }
            const blockedMoves = moveItems(`value-list-move-${value.internalCode}`, () => false, () => undefined, "valueLists.detail.rowActions.alphabeticalNoMove");
            const pinItem = {
                label: t("valueLists.detail.rowActions.pinToTop"),
                icon: "ri-pushpin-2-line",
                testId: `value-list-pin-${value.internalCode}`,
                onClick: () => togglePin(value.internalCode, true),
            };
            return [edit, ...blockedMoves, value.pinRank === null ? pinItem : unpinItem];
        }
        // ── Display drawer and save ──────────────────────────────────────────────────
        const editedCode = ref(null);
        const isDrawerOpen = ref(false);
        /** Read from the pending state, so the drawer reflects a change applied a moment ago. */
        const editedValue = computed(() => valuesStore.effectiveValues.find((value) => value.internalCode === editedCode.value));
        const editedLock = computed(() => editedValue.value
            ? visibilityLock(editedValue.value, valuesStore.effectiveValues, availableDefault.value)
            : null);
        function openDrawer(value) {
            editedCode.value = value.internalCode;
            isDrawerOpen.value = true;
        }
        function applyDisplay(displayed) {
            if (!editedCode.value)
                return;
            valuesStore.setDisplayed(editedCode.value, displayed, availableDefault.value);
            track("value_status_toggled", { catalogCode: code.value, displayed });
            if (isTwoLevelList.value) {
                // The row's level, not parentInternalCode: an orphan is shown, and counted, as a category.
                const edited = hierarchyRows.value.find((row) => row.value.internalCode === editedCode.value);
                const level = edited?.parent ? "subValue" : "category";
                track("value_list_hierarchy_status_toggled", { catalogCode: code.value, level, displayed });
            }
        }
        function onLockBlocked(reason) {
            track("value_status_lock_blocked", { catalogCode: code.value, reason });
        }
        const SAVE_ERROR_KEYS = {
            "CatalogValue.IsEssential": "valueLists.detail.errors.isEssential",
            "CatalogValue.IsDefault": "valueLists.detail.errors.isDefault",
            "CatalogValue.ParentIsHidden": "valueLists.detail.errors.parentIsHidden",
            "CatalogValue.Visibility.LastDisplayed": "valueLists.detail.errors.lastDisplayed",
            "CatalogValue.Visibility.Deprecated": "valueLists.detail.errors.deprecated",
            "CatalogValue.Visibility.ChildIsEssential": "valueLists.detail.errors.childIsEssential",
            "CatalogValue.Visibility.ChildIsDefault": "valueLists.detail.errors.childIsDefault",
            "CatalogValue.Order.NotManual": "valueLists.detail.errors.orderNotManual",
            "CatalogValue.Pin.NotAlphabetical": "valueLists.detail.errors.pinNotAlphabetical",
            "CatalogValue.Pin.RanksNotContiguous": "valueLists.detail.errors.pinRanks",
            "Catalog.Concurrency.Conflict": "valueLists.detail.errors.concurrentChange",
        };
        const lastSaveErrorKey = ref(SAVE_ERROR_FALLBACK);
        const showUnsavedChangesModal = ref(false);
        const { isDirty, isSaving, register, saveAll, proceedNavigation, cancelNavigation } = useSaveOrchestrator({
            successToastKey: "valueLists.detail.saved",
            successToastCount: () => valuesStore.pendingCount,
            blocErrorToastKey: () => lastSaveErrorKey.value,
            onNavigationBlocked: () => {
                showUnsavedChangesModal.value = true;
                track("value_status_leave_dialog_shown", { catalogCode: code.value });
            },
        });
        async function saveValues() {
            const count = valuesStore.pendingCount;
            const { visibility, order, pins } = valuesStore.pendingSummary;
            try {
                await valuesStore.savePending(code.value);
            }
            catch (error) {
                lastSaveErrorKey.value = resolveApiErrorKey(error, SAVE_ERROR_KEYS, SAVE_ERROR_FALLBACK);
                throw error;
            }
            clearHighlight();
            track("value_status_changes_saved", {
                catalogCode: code.value,
                count,
                visibilityCount: visibility.length,
                orderCount: order.length,
                pinCount: pins.length,
            });
        }
        register({
            id: "values",
            isDirty: computed(() => valuesStore.hasPending),
            save: saveValues,
        });
        // The view is reused from one list to the next, and loading the next list drops the pending
        // changes; the orchestrator only guards leaving the route, so the switch is guarded here.
        let pendingListSwitch = null;
        onBeforeRouteUpdate((to, from, next) => {
            if (to.params.code === from.params.code || !isDirty.value)
                return next();
            pendingListSwitch = next;
            showUnsavedChangesModal.value = true;
            track("value_status_leave_dialog_shown", { catalogCode: code.value });
        });
        function handleStay() {
            showUnsavedChangesModal.value = false;
            cancelNavigation();
            pendingListSwitch?.(false);
            pendingListSwitch = null;
        }
        // A refused save stays dirty (saveAll toasts the reason), so the modal stays open for a retry.
        async function handleSaveAndContinue() {
            await saveAll();
            if (isDirty.value)
                return;
            showUnsavedChangesModal.value = false;
            proceedNavigation();
            pendingListSwitch?.();
            pendingListSwitch = null;
        }
        function rowActionsLabel(value) {
            return t("valueLists.detail.rowActions.menu", { value: resolveLabel(value.labels, locale.value).text });
        }
        const STATUS_BADGES = {
            alwaysDisplayed: { theme: "green", key: "valueLists.detail.status.alwaysDisplayed", icon: "ri-lock-line" },
            displayed: { theme: "green", key: "valueLists.detail.status.displayed" },
            hidden: { theme: "disabled", key: "valueLists.detail.status.hidden" },
            notOffered: { theme: "neutral", key: "valueLists.detail.status.notOffered" },
        };
        /** A hidden value reads struck through and subdued: never colour alone. */
        function identityText(value, text) {
            const row = rowByCode.value.get(value.internalCode);
            const shown = row ? effectiveValue(row) : value;
            if (isDisplayed(shown, availableDefault.value) || value.isDeprecated)
                return text;
            return h("span", {
                class: "line-through text-(--colors-text-body-subtle)",
                "data-testid": `value-list-hidden-${value.internalCode}`,
            }, text);
        }
        /** A sub-value sits indented under a tree connector, and a screen reader hears its category. */
        function levelMarker(value) {
            const parent = rowByCode.value.get(value.internalCode)?.parent;
            if (!parent)
                return [];
            return [
                h("span", {
                    class: "ml-1 inline-block h-3 w-3 shrink-0 -translate-y-1 rounded-bl-sm border-b border-l border-(--border-border-base-medium)",
                    "aria-hidden": "true",
                    "data-testid": `value-list-level-${value.internalCode}`,
                }),
                h("span", { class: "sr-only" }, t("valueLists.detail.hierarchy.subValueOf", { category: resolveLabel(parent.labels, locale.value).text })),
            ];
        }
        /**
         * A pinned copy carries the "épinglée" badge, and its category for a sub-value, since it sits away
         * from that category. The pinned value's own line says so to a screen reader; its pin icon is visual.
         */
        function pinnedCopyCell(value) {
            const parent = lineByCode.value.get(value.internalCode)?.parent;
            return h("span", { class: "inline-flex items-center gap-2" }, [
                resolveLabel(value.labels, locale.value).text,
                parent
                    ? h("span", { class: "text-(--colors-text-body-subtle)" }, `· ${resolveLabel(parent.labels, locale.value).text}`)
                    : null,
                h(Badge, {
                    label: t("valueLists.detail.pin.badge"),
                    theme: "neutral",
                    leftIcon: "ri-pushpin-fill",
                    "data-testid": `value-list-pinned-badge-${value.internalCode}`,
                }),
            ]);
        }
        function identityCell(value) {
            if (value.pinCopy)
                return pinnedCopyCell(value);
            const resolved = resolveLabel(value.labels, locale.value);
            return h("span", { class: "inline-flex items-center gap-2" }, [
                ...levelMarker(value),
                identityText(value, resolved.text),
                resolved.isFallback && resolved.language
                    ? h(Badge, {
                        label: t("valueLists.detail.fallback", { code: resolved.language.toUpperCase() }),
                        theme: "neutral",
                        "data-testid": `value-list-fallback-${value.internalCode}`,
                    })
                    : null,
                isPinShown(value) ? h("span", { class: "sr-only" }, t("valueLists.detail.pin.pinned")) : null,
            ]);
        }
        /**
         * The path, with its scope ("Position 1 sur 2 — valeurs de …") as a tooltip and for screen readers.
         * Not a tab stop, a deliberate trade-off: one stop per row would sit before the pager. A keyboard
         * user still sees the category (the row above) and the rank (the path); only the sibling count
         * stays in the tooltip, and screen readers get it from the sr-only text.
         */
        function positionCell(value) {
            // A pinned copy shows its rank among the pins; an alphabetical value has no stored rank to show.
            if (value.pinCopy)
                return String(value.pinRank ?? "");
            const row = rowByCode.value.get(value.internalCode);
            if (!row?.position)
                return "";
            if (!isTwoLevelList.value)
                return row.position;
            const scope = row.parent
                ? t("valueLists.detail.hierarchy.positionInCategory", {
                    rank: row.rank,
                    total: row.siblings,
                    category: resolveLabel(row.parent.labels, locale.value).text,
                })
                : t("valueLists.detail.hierarchy.position", { rank: row.rank, total: row.siblings });
            return h(Tooltip, { text: scope, as: "span", labelsTrigger: true, "data-testid": `value-list-position-${value.internalCode}` }, () => [h("span", { "aria-hidden": "true" }, row.position), h("span", { class: "sr-only" }, scope)]);
        }
        function statusCell(value) {
            const row = (value.pinCopy ? lineByCode : rowByCode).value.get(value.internalCode);
            const badge = STATUS_BADGES[valueStatus(row ? effectiveValue(row) : value, availableDefault.value)];
            return h(Badge, {
                type: "with-dot",
                theme: badge.theme,
                label: t(badge.key),
                leftIcon: badge.icon,
            });
        }
        const columns = computed(() => [
            {
                id: "identity",
                header: columnHeaders.value.identity,
                cell: ({ row }) => identityCell(row.original),
            },
            ...secondaryLanguages(locale.value).map((language) => ({
                id: languageColumnId(language),
                header: columnHeaders.value[languageColumnId(language)],
                cell: ({ row }) => row.original.labels[language],
            })),
            {
                id: "externalCode",
                header: columnHeaders.value.externalCode,
                cell: ({ row }) => row.original.externalCode,
            },
            {
                id: "internalCode",
                header: columnHeaders.value.internalCode,
                cell: ({ row }) => row.original.internalCode,
            },
            {
                id: "status",
                header: columnHeaders.value.status,
                cell: ({ row }) => statusCell(row.original),
            },
            {
                id: "position",
                header: columnHeaders.value.position,
                cell: ({ row }) => positionCell(row.original),
            },
        ]);
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                (SidebarComponent.value)
                    ? (_openBlock(), _createBlock(_resolveDynamicComponent(SidebarComponent.value), { key: 0 }))
                    : _createCommentVNode("v-if", true),
                _createElementVNode("main", _hoisted_2, [
                    _createElementVNode("div", _hoisted_3, [
                        _createVNode(_unref(InlineLink), {
                            as: _unref(RouterLink),
                            "icon-left": "ri-arrow-left-line",
                            to: { name: 'ValueListManager' },
                            "data-testid": "value-list-detail-back"
                        }, {
                            default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(_unref(t)("valueLists.detail.back")), 1 /* TEXT */)
                            ]),
                            _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["as"])
                    ]),
                    _createElementVNode("div", _hoisted_4, [
                        _createElementVNode("div", _hoisted_5, [
                            _createElementVNode("h1", _hoisted_6, _toDisplayString(title.value), 1 /* TEXT */),
                            (list.value)
                                ? (_openBlock(), _createBlock(_unref(Badge), {
                                    key: 0,
                                    label: list.value.code,
                                    "text-only": "",
                                    "data-testid": "value-list-detail-code"
                                }, null, 8 /* PROPS */, ["label"]))
                                : _createCommentVNode("v-if", true),
                            (list.value)
                                ? (_openBlock(), _createBlock(_unref(Button), {
                                    key: 1,
                                    class: "ml-auto",
                                    variant: "primary",
                                    label: _unref(t)('form.save'),
                                    disabled: !_unref(valuesStore).hasPending || _unref(isSaving),
                                    loading: _unref(isSaving),
                                    "data-testid": "value-list-detail-save",
                                    onClick: _unref(saveAll)
                                }, null, 8 /* PROPS */, ["label", "disabled", "loading", "onClick"]))
                                : _createCommentVNode("v-if", true)
                        ]),
                        _createElementVNode("p", _hoisted_7, _toDisplayString(_unref(t)("valueLists.detail.subtitle")), 1 /* TEXT */)
                    ]),
                    (isUnknownList.value)
                        ? (_openBlock(), _createBlock(ModuleState, {
                            key: 0,
                            type: "empty",
                            "data-testid": "value-list-detail-not-found",
                            title: _unref(t)('valueLists.detail.notFound')
                        }, null, 8 /* PROPS */, ["title"]))
                        : (_unref(valuesStore).loadError || _unref(listsStore).loadError)
                            ? (_openBlock(), _createBlock(ModuleState, {
                                key: 1,
                                type: "error",
                                "data-testid": "value-list-detail-error",
                                title: _unref(t)('valueLists.detail.loadError'),
                                "cta-icon": "ri-refresh-line",
                                "cta-label": _unref(t)('common.retry'),
                                onCta: reload
                            }, null, 8 /* PROPS */, ["title", "cta-label"]))
                            : (_openBlock(), _createBlock(_unref(TableShell), {
                                key: 2,
                                query: _unref(tableQuery),
                                views: _unref(tableViews),
                                labels: tableLabels.value,
                                permissions: { canSaveView: false, canShareView: false },
                                "tab-dropdowns": _unref(tabDropdowns),
                                "column-settings": _unref(columnSettings),
                                "search-placeholder": _unref(t)('valueLists.detail.searchPlaceholder'),
                                loading: isLoading.value,
                                "show-view-selector": "",
                                bordered: ""
                            }, {
                                filter: _withCtx(() => [
                                    _createVNode(_unref(TableFilterSelector), _mergeProps(_unref(filterSelectorLabels), {
                                        fields: filterFields.value,
                                        "active-filters": {},
                                        "trigger-label": _unref(t)('table.filter'),
                                        "trigger-icon-left": "ri-filter-2-line",
                                        "step1-search-placeholder": _unref(t)('filters.searchPlaceholder'),
                                        "confirm-label": _unref(t)('table.tabs.apply'),
                                        "back-label": _unref(t)('common.back'),
                                        "advanced-filters-label": "",
                                        "no-results-label": _unref(t)('common.noResults'),
                                        "data-testid": "value-list-detail-filter"
                                    }), null, 16 /* FULL_PROPS */, ["fields", "trigger-label", "step1-search-placeholder", "confirm-label", "back-label", "no-results-label"])
                                ]),
                                "controls-info": _withCtx(() => [
                                    _createVNode(_unref(Popover), {
                                        header: orderLabel.value,
                                        position: "bottom",
                                        "max-width": "300px"
                                    }, {
                                        trigger: _withCtx(({ triggerProps }) => [
                                            _createElementVNode("button", _mergeProps(triggerProps, {
                                                type: "button",
                                                class: "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-(--colors-text-heading) hover:bg-(--colors-background-hover) focus-visible:outline-2 focus-visible:outline-(--colors-text-accent)",
                                                "data-testid": "value-list-detail-order"
                                            }), [
                                                _createTextVNode(_toDisplayString(orderLabel.value) + " ", 1 /* TEXT */),
                                                _cache[2] || (_cache[2] = _createElementVNode("i", {
                                                    class: "ri-information-line text-sm text-(--colors-text-body-subtle)",
                                                    "aria-hidden": "true"
                                                }, null, -1 /* CACHED */))
                                            ], 16 /* FULL_PROPS */)
                                        ]),
                                        default: _withCtx(() => [
                                            _createElementVNode("p", _hoisted_8, _toDisplayString(orderDetail.value), 1 /* TEXT */),
                                            (sortMode.value === 'Manual')
                                                ? (_openBlock(), _createBlock(_unref(Button), {
                                                    key: 0,
                                                    class: "mt-3",
                                                    variant: "secondary",
                                                    label: _unref(t)('valueLists.detail.order.restoreDefault'),
                                                    disabled: _unref(isDefaultOrder)(_unref(valuesStore).effectiveValues),
                                                    "data-testid": "value-list-detail-restore-order",
                                                    onClick: restoreDefaultOrder
                                                }, null, 8 /* PROPS */, ["label", "disabled"]))
                                                : _createCommentVNode("v-if", true)
                                        ]),
                                        _: 1 /* STABLE */
                                    }, 8 /* PROPS */, ["header"])
                                ]),
                                table: _withCtx(() => [
                                    _createVNode(_unref(DataTable), {
                                        columns: columns.value,
                                        data: pageRows.value,
                                        "column-visibility": _unref(columnSettings).visibility,
                                        "column-order": _unref(columnSettings).order.value,
                                        "column-pinning": _unref(columnSettings).pinning.value,
                                        "has-settings": "",
                                        "sticky-header": "",
                                        "settings-aria-label": _unref(t)('columnSettings.title'),
                                        "empty-text": _unref(t)(searchTerm.value ? 'valueLists.detail.noSearchResult' : 'valueLists.detail.empty'),
                                        "region-label": title.value,
                                        "is-row-muted": isRowMuted,
                                        "is-row-highlighted": isRowHighlighted,
                                        onSettingsClick: _unref(columnSettings).openDrawer,
                                        "onUpdate:columnOrder": _unref(columnSettings).setOrder,
                                        "onUpdate:columnPinning": _unref(columnSettings).setPinning
                                    }, {
                                        "row-end": _withCtx(({ row }) => [
                                            _createElementVNode("div", {
                                                class: "flex opacity-0 transition-opacity [tr:hover_&]:opacity-100 focus-within:opacity-100 has-[[aria-expanded=true]]:opacity-100 [@media(hover:none)]:opacity-100",
                                                "data-testid": `value-list-actions-cell-${rowTestId(row)}`
                                            }, [
                                                (isPinShown(row))
                                                    ? (_openBlock(), _createElementBlock("i", {
                                                        key: 0,
                                                        class: "ri-pushpin-fill mr-1 self-center text-(--colors-text-accent)",
                                                        "aria-hidden": "true",
                                                        "data-testid": `value-list-pin-icon-${rowTestId(row)}`
                                                    }, null, 8 /* PROPS */, _hoisted_10))
                                                    : _createCommentVNode("v-if", true),
                                                _createVNode(_unref(Dropdown), {
                                                    items: rowActions(row),
                                                    "menu-align": "right",
                                                    "aria-label": rowActionsLabel(row),
                                                    "data-testid": `value-list-actions-${rowTestId(row)}`
                                                }, {
                                                    trigger: _withCtx(({ triggerProps }) => [
                                                        _createVNode(_unref(Button), _mergeProps(triggerProps, {
                                                            variant: "ghost",
                                                            size: "md",
                                                            "icon-only": "",
                                                            "icon-left": "ri-more-fill",
                                                            "aria-label": rowActionsLabel(row),
                                                            "data-testid": `value-list-actions-trigger-${rowTestId(row)}`
                                                        }), null, 16 /* FULL_PROPS */, ["aria-label", "data-testid"])
                                                    ]),
                                                    _: 2 /* DYNAMIC */
                                                }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["items", "aria-label", "data-testid"])
                                            ], 8 /* PROPS */, _hoisted_9)
                                        ]),
                                        _: 1 /* STABLE */
                                    }, 8 /* PROPS */, ["columns", "data", "column-visibility", "column-order", "column-pinning", "settings-aria-label", "empty-text", "region-label", "onSettingsClick", "onUpdate:columnOrder", "onUpdate:columnPinning"])
                                ]),
                                pagination: _withCtx(() => [
                                    (filteredRows.value.length > 0)
                                        ? (_openBlock(), _createBlock(_unref(Pager), _mergeProps({
                                            key: 0,
                                            "current-page": _unref(tableQuery).state.page,
                                            "total-pages": totalPages.value,
                                            "total-items": filteredRows.value.length,
                                            "items-per-page": _unref(tableQuery).state.pageSize
                                        }, pagerLabels.value, {
                                            "rows-per-page-options": ROW_OPTIONS,
                                            "show-go-to-page": "",
                                            onPageChange: _unref(tableQuery).setPage,
                                            onItemsPerPageChange: _unref(tableQuery).setPageSize
                                        }), null, 16 /* FULL_PROPS */, ["current-page", "total-pages", "total-items", "items-per-page", "onPageChange", "onItemsPerPageChange"]))
                                        : _createCommentVNode("v-if", true)
                                ]),
                                _: 1 /* STABLE */
                            }, 8 /* PROPS */, ["query", "views", "labels", "tab-dropdowns", "column-settings", "search-placeholder", "loading"]))
                ]),
                _createElementVNode("p", _hoisted_11, _toDisplayString(announcement.value), 1 /* TEXT */),
                _createVNode(ValueDisplayDrawer, {
                    modelValue: isDrawerOpen.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((isDrawerOpen).value = $event)),
                    title: editedValue.value ? _unref(resolveLabel)(editedValue.value.labels, _unref(locale)).text : '',
                    displayed: editedValue.value ? _unref(isDisplayed)(editedValue.value, availableDefault.value) : false,
                    lock: editedLock.value,
                    onApply: applyDisplay,
                    onLockBlocked: onLockBlocked
                }, null, 8 /* PROPS */, ["modelValue", "title", "displayed", "lock"]),
                _createVNode(_unref(UnsavedChangesModal), {
                    modelValue: showUnsavedChangesModal.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((showUnsavedChangesModal).value = $event)),
                    title: _unref(t)('leaveWithoutSaving.title'),
                    message: _unref(t)('valueLists.detail.leave.message'),
                    "stay-label": _unref(t)('valueLists.detail.leave.keepEditing'),
                    "leave-label": _unref(t)('valueLists.detail.leave.keepEditing'),
                    "save-label": _unref(t)('leaveWithoutSaving.save'),
                    "save-disabled": _unref(isSaving),
                    onDiscard: handleStay,
                    onStay: handleStay,
                    onSave: handleSaveAndContinue
                }, null, 8 /* PROPS */, ["modelValue", "title", "message", "stay-label", "leave-label", "save-label", "save-disabled"])
            ]));
        };
    }
});
